import React, { useEffect, useMemo, useState } from 'react';
import { CheckOutlined, CloseOutlined, ReloadOutlined, ScissorOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import GamePlayerPortrait from '../components/GamePlayerPortrait';
import LoadingSpinner from '../components/LoadingSpinner';
import { samplePlayers } from '../games/gameLogic';
import { useGamesData } from '../hooks/useGamesData';
import { GamePlayer, KeepCutCategory } from '../types/games';
import './Games.css';

type Choice = 'keep' | 'cut';
interface Decision { player: GamePlayer; choice: Choice }

const KeepCutPlayer: React.FC<{
  player: GamePlayer;
  compact?: boolean;
  showPpg?: boolean;
  showFinalRank?: boolean;
}> = ({ player, compact = false, showPpg = false, showFinalRank = false }) => (
  <div className={`keep-cut-player ${compact ? 'is-compact' : ''}`}>
    <GamePlayerPortrait name={player.Name} position={player.Position} url={player.HeadshotUrl} />
    <div>
      <span className={`game-position game-position--${player.Position.toLowerCase()}`}>{player.Position}</span>
      <strong>{player.Name}</strong>
      {showPpg && player.GamesStarted > 0 && (
        <small>{player.PointsPerGame.toFixed(1)} PPG · {player.GamesStarted} starts</small>
      )}
      {showFinalRank && (
        <small className="keep-cut-final-rank">
          {player.SeasonPositionRank
            ? `Finished ${player.Position}${player.SeasonPositionRank}`
            : 'Final positional rank unavailable'}
        </small>
      )}
    </div>
  </div>
);

const KeepCutGame: React.FC = () => {
  const { data, isLoading, error } = useGamesData();
  const [categoryId, setCategoryId] = useState('');
  const [board, setBoard] = useState<GamePlayer[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);

  useEffect(() => {
    if (!categoryId && data?.KeepCutCategories.length) {
      setCategoryId(data.KeepCutCategories[Math.floor(Math.random() * data.KeepCutCategories.length)].Id);
    }
  }, [categoryId, data]);

  const category = data?.KeepCutCategories.find((item) => item.Id === categoryId);
  const keepers = decisions.filter((decision) => decision.choice === 'keep');
  const cuts = decisions.filter((decision) => decision.choice === 'cut');
  const currentPlayer = board[decisions.length];
  const complete = board.length === 8 && decisions.length === 8;

  const groupedCategories = useMemo(() => ({
    keepers: data?.KeepCutCategories.filter((item) => item.Type === 'keepers') || [],
    franchises: data?.KeepCutCategories.filter((item) => item.Type === 'franchise') || [],
  }), [data]);

  const startGame = (selected: KeepCutCategory | undefined = category) => {
    if (!selected) return;
    setBoard(samplePlayers(selected.Players));
    setDecisions([]);
  };

  const decide = (choice: Choice) => {
    if (!currentPlayer || (choice === 'keep' && keepers.length >= 4) || (choice === 'cut' && cuts.length >= 4)) return;
    setDecisions((current) => [...current, { player: currentPlayer, choice }]);
  };

  if (isLoading) return <div className="games-page"><LoadingSpinner message="Shuffling league history…" /></div>;
  if (error || !data) return <div className="games-page"><EmptyState message="Game data is unavailable" description="Run the game-data export and refresh this page." /></div>;

  if (!board.length) {
    return (
      <div className="games-page keep-cut-page">
        <Link to="/games" className="games-back">← League Games</Link>
        <header className="games-hero games-hero--compact">
          <div className="games-eyebrow"><ScissorOutlined /> No wrong answers</div>
          <h1>Keep 4, Cut 4</h1>
          <p>Pick a pool. Eight random players will appear one at a time—and you must finish with four on each side.</p>
        </header>
        <section className="game-setup-card">
          <label htmlFor="keep-cut-category">Choose your player pool</label>
          <select id="keep-cut-category" value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            <optgroup label="Keeper classes">
              {groupedCategories.keepers.map((item) => <option value={item.Id} key={item.Id}>{item.Name}</option>)}
            </optgroup>
            <optgroup label="Franchise history">
              {groupedCategories.franchises.map((item) => <option value={item.Id} key={item.Id}>{item.Name}</option>)}
            </optgroup>
          </select>
          {category && <p>{category.Description} The eight-player draw changes every game.</p>}
          <button type="button" className="game-primary-button" onClick={() => startGame()}>Deal eight players</button>
        </section>
      </div>
    );
  }

  if (complete) {
    return (
      <div className="games-page keep-cut-page">
        <Link to="/games" className="games-back">← League Games</Link>
        <header className="keep-cut-result-header">
          <span>{category?.Name}</span>
          <h1>Your verdict is in.</h1>
          <p>No consensus rankings. No appeals.</p>
        </header>
        <div className="keep-cut-results">
          <section className="keep-cut-result-column is-keep">
            <h2><CheckOutlined /> Keep</h2>
            {keepers.map(({ player }) => (
              <KeepCutPlayer
                player={player}
                compact
                showPpg={category?.Type === 'franchise'}
                showFinalRank={category?.Type === 'keepers'}
                key={player.PlayerId}
              />
            ))}
          </section>
          <section className="keep-cut-result-column is-cut">
            <h2><CloseOutlined /> Cut</h2>
            {cuts.map(({ player }) => (
              <KeepCutPlayer
                player={player}
                compact
                showPpg={category?.Type === 'franchise'}
                showFinalRank={category?.Type === 'keepers'}
                key={player.PlayerId}
              />
            ))}
          </section>
        </div>
        <div className="game-result-actions">
          <button type="button" className="game-primary-button" onClick={() => startGame()}><ReloadOutlined /> New eight</button>
          <button type="button" className="game-secondary-button" onClick={() => { setBoard([]); setDecisions([]); }}>Change category</button>
        </div>
      </div>
    );
  }

  return (
    <div className="games-page keep-cut-page">
      <div className="keep-cut-play-header">
        <div><Link to="/games" className="games-back">← Exit game</Link><strong>{category?.Name}</strong></div>
        <span>{decisions.length + 1} <small>of 8</small></span>
      </div>
      <div className="keep-cut-progress" aria-label={`${decisions.length} of 8 decisions made`}>
        {Array.from({ length: 8 }, (_, index) => <span className={index < decisions.length ? 'is-done' : index === decisions.length ? 'is-current' : ''} key={index} />)}
      </div>
      <section className="keep-cut-stage">
        <div className="keep-cut-counter is-keep"><span>KEEP</span><strong>{keepers.length}</strong><small>{keepers.length === 4 ? 'side full' : `${4 - keepers.length} left`}</small></div>
        <article className="keep-cut-reveal" key={currentPlayer.PlayerId}>
          <span className={`game-position game-position--${currentPlayer.Position.toLowerCase()}`}>{currentPlayer.Position}</span>
          <GamePlayerPortrait name={currentPlayer.Name} position={currentPlayer.Position} url={currentPlayer.HeadshotUrl} />
          <h1>{currentPlayer.Name}</h1>
          {category?.Type !== 'franchise' && currentPlayer.GamesStarted > 0 && (
            <p>{currentPlayer.PointsPerGame.toFixed(1)} PPG across {currentPlayer.GamesStarted} starts</p>
          )}
          <small>Player {decisions.length + 1} of 8</small>
        </article>
        <div className="keep-cut-counter is-cut"><span>CUT</span><strong>{cuts.length}</strong><small>{cuts.length === 4 ? 'side full' : `${4 - cuts.length} left`}</small></div>
      </section>
      <div className="keep-cut-actions">
        <button type="button" className="keep-button" disabled={keepers.length === 4} onClick={() => decide('keep')}><CheckOutlined /> Keep</button>
        <button type="button" className="cut-button" disabled={cuts.length === 4} onClick={() => decide('cut')}><CloseOutlined /> Cut</button>
      </div>
      {(keepers.length === 4 || cuts.length === 4) && <p className="keep-cut-nudge">Your {keepers.length === 4 ? 'keep' : 'cut'} side is full. The rest must go the other way.</p>}
    </div>
  );
};

export default KeepCutGame;
